import React from 'react';

export const INK = '#2b2622';
const CLOTHES = ['#efe9df', '#d6cfc2', '#9aa0a6', '#3d4652', '#e7e2d8', '#6b5b4b', '#2f3236', '#c6b7a1', '#f4f1ea', '#5a6470'];
const BOOKS = ['#6b4f3a', '#2f3a44', '#a68966', '#d8cfc0', '#3e4a3c', '#8b3a2f', '#1f1f1f', '#bfae93', '#55606b'];
const rnd = (s) => { const x = Math.sin(s * 9301 + 49297) * 233280; return x - Math.floor(x); };

export const makeCtx = (p, mats, led, technical, facade = 'lisse') => ({
  p, led, mats, facade,
  F: (id) => `url(#${p}-${id})`,
  front: mats.front, accent: mats.accent === 'same' ? mats.front : mats.accent,
  sw: technical ? 2.5 : 4.2,
  illus: !technical,
});

const Sheen = ({ x, y, w, h, c }) => (c.illus ? <rect x={x} y={y} width={w} height={h} fill={`url(#${c.p}-sheen)`} pointerEvents="none" /> : null);

const Interior = ({ x, y, w, h, c, glow = true }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill={c.F(c.front)} />
    <rect x={x} y={y} width={w} height={h} fill="#000" opacity="0.32" />
    {c.illus && <rect x={x} y={y} width={w} height={h} fill={`url(#${c.p}-niche)`} />}
    {c.led && glow && <rect x={x} y={y} width={w} height={Math.min(c.illus ? 220 : 140, h * 0.6)} fill={`url(#${c.p}-led)`} opacity={c.illus ? 1 : 0.85} />}
    {c.led && glow && c.illus && <>
      <rect x={x} y={y} width={Math.min(70, w * 0.15)} height={h} fill={`url(#${c.p}-ledL)`} />
      <rect x={x + w - Math.min(70, w * 0.15)} y={y} width={Math.min(70, w * 0.15)} height={h} fill={`url(#${c.p}-ledR)`} />
      <rect x={x + 8} y={y + 4} width={w - 16} height={10} rx={5} fill="#fff3d6" opacity="0.95" />
    </>}
  </g>
);

const Shelf = ({ x, y, w, c }) => (
  <g>
    <rect x={x} y={y} width={w} height={22} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw * 0.6} />
    {c.led && <rect x={x + 10} y={y + 22} width={w - 20} height={90} fill={`url(#${c.p}-led)`} opacity="0.7" />}
  </g>
);

function ShelfItems({ x, y, w, h, seed, c }) {
  if (w < 220 || h < 160) return null;
  const r = rnd(seed);
  const items = [];
  if (r < 0.3 && c.illus) {
    const cols = ['#e9e4dc', '#9aa0a6', '#3d4652', '#c6b7a1', '#2f3236', '#d6cfc2'];
    const sw = Math.min(260, w * 0.38), n = Math.max(2, Math.min(6, Math.floor((h - 10) / 34)));
    [x + 25, x + w - 25 - sw].forEach((sx, k) => (k === 0 || w > 520) && Array.from({ length: n }).forEach((_, i) => items.push(
      <rect key={`f${k}${i}`} x={sx + (i % 2) * 6} y={y + h - (i + 1) * 34} width={sw - 12} height={32} rx={10} fill={cols[(seed + i + k) % cols.length]} stroke={INK} strokeWidth={c.sw * 0.45} />,
    )));
  } else if (r < 0.35) {
    const bw = Math.min(260, w * 0.45), bh = Math.min(170, h * 0.55);
    items.push(<rect key="b1" x={x + 30} y={y + h - bh} width={bw} height={bh} fill="#ebe6dd" stroke={INK} strokeWidth={c.sw * 0.5} />);
    items.push(<rect key="b2" x={x + 40 + bw} y={y + h - bh * 0.75} width={bw * 0.8} height={bh * 0.75} fill="#3b3a38" stroke={INK} strokeWidth={c.sw * 0.5} />);
  } else if (r < 0.7) {
    let bx = x + 25;
    for (let i = 0; i < 9 && bx < x + w * 0.6; i++) {
      const bh = h * (0.55 + rnd(seed + i) * 0.3), bw = 22 + rnd(seed * 3 + i) * 22;
      items.push(<rect key={`k${i}`} x={bx} y={y + h - bh} width={bw} height={bh} fill={BOOKS[(seed + i) % BOOKS.length]} stroke={INK} strokeWidth={c.sw * 0.4} />);
      bx += bw + 2;
    }
    items.push(<path key="v" d={`M${x + w * 0.78} ${y + h} c-40 0 -50 -60 -30 -110 c10 -25 10 -45 6 -60 h48 c-4 15 -4 35 6 60 c20 50 10 110 -30 110z`} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={c.sw * 0.5} />);
  } else {
    const sh = Math.min(140, h * 0.5);
    items.push(<rect key="s1" x={x + w * 0.2} y={y + h - sh} width={w * 0.6} height={sh} fill="#d8d1c4" stroke={INK} strokeWidth={c.sw * 0.5} />);
    items.push(<path key="s2" d={`M${x + w * 0.2} ${y + h - sh * 0.5} h${w * 0.6}`} stroke={INK} strokeWidth={c.sw * 0.4} />);
  }
  return <g>{items}</g>;
}

function Garments({ x, y, w, h, long, seed, c }) {
  const n = Math.max(1, Math.floor((w - 70) / 46));
  const g = [<rect key="rod" x={x + 15} y={y + 45} width={w - 30} height={12} rx={6} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={c.sw * 0.5} />];
  for (let i = 0; i < n; i++) {
    const gx = x + 35 + i * ((w - 70) / n);
    const L = h * (long ? 0.86 : 0.7) * (0.9 + rnd(seed + i) * 0.12);
    const col = CLOTHES[(seed * 2 + i) % CLOTHES.length];
    g.push(
      <g key={i}>
        <path d={`M${gx + 20} ${y + 45} q-8 -16 4 -22`} stroke={INK} strokeWidth={c.sw * 0.5} fill="none" />
        <path d={`M${gx + 2} ${y + 72} l18 -12 l18 12 l3 ${L - 30} q-21 10 -42 0z`} fill={col} stroke={INK} strokeWidth={c.sw * 0.45} />
        {c.illus && <path d={`M${gx + 2} ${y + 72} l18 -12 l18 12 l3 ${L - 30} q-21 10 -42 0z`} fill={`url(#${c.p}-cloth)`} />}
        {c.illus && <path d={`M${gx + 12} ${y + 66} l8 22 l8 -22 M${gx + 20} ${y + 90} v${L * 0.6}`} stroke={INK} strokeWidth={c.sw * 0.3} fill="none" opacity="0.6" />}
      </g>
    );
  }
  return <g>{g}</g>;
}

const Handle = ({ x, y, w, h, c }) => <rect x={x} y={y} width={w} height={h} rx={Math.min(w, h) / 2} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={c.sw * 0.5} />;

// Door / drawer front drawn in the chosen facade collection
function FrontSvg({ x, y, w, h, mat, c, handle }) {
  const f = c.facade || 'lisse', s = c.sw;
  const base = <rect x={x} y={y} width={w} height={h} fill={c.F(mat)} stroke={INK} strokeWidth={s} />;
  let deco = null;
  if (f === 'cannele') {
    const n = Math.max(2, Math.floor(w / 32));
    deco = <g pointerEvents="none">{Array.from({ length: n - 1 }).map((_, i) => {
      const lx = x + (w / n) * (i + 1);
      return <g key={i}><path d={`M${lx} ${y + 6}V${y + h - 6}`} stroke={INK} strokeWidth={s * 0.35} opacity="0.35" /><path d={`M${lx + 5} ${y + 6}V${y + h - 6}`} stroke="#fff" strokeWidth={s * 0.5} opacity="0.18" /></g>;
    })}</g>;
  } else if (f === 'cadre' || f === 'filet') {
    const i = f === 'cadre' ? Math.min(50, w * 0.2, h * 0.2) : Math.min(12, w * 0.1, h * 0.1);
    deco = <g pointerEvents="none"><rect x={x + i} y={y + i} width={w - 2 * i} height={h - 2 * i} fill="#000" opacity="0.06" stroke={INK} strokeWidth={s * 0.6} />{f === 'cadre' && <path d={`M${x + i} ${y + h - i}h${w - 2 * i}`} stroke="#fff" strokeWidth={s * 0.6} opacity="0.3" />}</g>;
  } else if (f === 'gorge') {
    return <g><rect x={x} y={y + 28} width={w} height={h - 28} fill={c.F(mat)} stroke={INK} strokeWidth={s} /><rect x={x} y={y} width={w} height={28} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s * 0.6} /><Sheen x={x} y={y + 28} w={w} h={h - 28} c={c} /></g>;
  } else if (f === 'verre' && h > 300 && !handle?.drawer) {
    deco = <rect x={x + 20} y={y + 20} width={w - 40} height={h - 40} fill={`url(#${c.p}-glass)`} stroke={c.F(c.mats.metal)} strokeWidth={14} />;
  }
  return <g>{base}{deco}<Sheen x={x} y={y} w={w} h={h} c={c} />{handle && <Handle x={handle.x} y={handle.y} w={handle.w} h={handle.h} c={c} />}</g>;
}

function Part({ k, x, y, w, h, c, seed, hpos }) {
  const g = 4, s = c.sw;
  switch (k) {
    case 'door': {
      const n = w > 650 ? 2 : 1, dw = (w - g * (n + 1)) / n, hh = Math.min(200, h * 0.3);
      const hy = hpos === 'top' ? y + 45 : hpos === 'bottom' ? y + h - 45 - hh : y + h * 0.4 - hh / 2;
      return (
        <g>
          {Array.from({ length: n }).map((_, i) => {
            const dx = x + g + i * (dw + g);
            const hx = n === 2 ? (i === 0 ? dx + dw - 40 : dx + 28) : dx + dw - 40;
            return (
              <g key={i}>
                <FrontSvg x={dx} y={y + g} w={dw} h={h - 2 * g} mat={c.front} c={c} handle={{ x: hx, y: hy, w: 12, h: hh }} />
              </g>
            );
          })}
        </g>
      );
    }
    case 'drawer': {
      const hw = Math.max(110, Math.min(360, w * 0.38));
      return (
        <g>
          <FrontSvg x={x + g} y={y + g} w={w - 2 * g} h={h - 2 * g} mat={c.accent} c={c} handle={{ x: x + (w - hw) / 2, y: y + Math.min(45, h * 0.25), w: hw, h: 12, drawer: true }} />
        </g>
      );
    }
    case 'open': {
      const n = Math.max(1, Math.round(h / 380)), sp = h / n;
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} />
          {Array.from({ length: n }).map((_, i) => (
            <g key={i}>
              {i > 0 && <Shelf x={x} y={y + i * sp - 11} w={w} c={c} />}
              <ShelfItems x={x} y={y + i * sp + 12} w={w} h={sp - 24} seed={seed * 7 + i} c={c} />
            </g>
          ))}
        </g>
      );
    }
    case 'hang':
    case 'hangLong':
      return <g><Interior x={x} y={y} w={w} h={h} c={c} /><Garments x={x} y={y} w={w} h={h} long={k === 'hangLong'} seed={seed} c={c} /></g>;
    case 'glass': {
      const n = Math.max(2, Math.round(h / 330)), sp = h / n;
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} />
          {Array.from({ length: n }).map((_, i) => (
            <g key={i}>
              {i > 0 && <path d={`M${x} ${y + i * sp}h${w}`} stroke="#cfe3e6" strokeWidth={10} opacity="0.8" />}
              <ShelfItems x={x} y={y + i * sp + 10} w={w} h={sp - 20} seed={seed * 5 + i + 3} c={c} />
            </g>
          ))}
          <rect x={x + g} y={y + g} width={w - 2 * g} height={h - 2 * g} fill={`url(#${c.p}-glass)`} stroke={c.F(c.mats.metal)} strokeWidth={14} />
          <path d={`M${x + w * 0.2} ${y + h * 0.15} l${w * 0.25} -${h * 0.1} M${x + w * 0.3} ${y + h * 0.28} l${w * 0.4} -${h * 0.18}`} stroke="#fff" strokeWidth={6} opacity="0.5" />
        </g>
      );
    }
    case 'oven':
    case 'micro':
      return (
        <g>
          <rect x={x + g} y={y + g} width={w - 2 * g} height={h - 2 * g} fill="#1d1d1e" stroke={INK} strokeWidth={s} />
          <rect x={x + 50} y={y + (k === 'oven' ? 120 : 70)} width={k === 'oven' ? w - 100 : w * 0.62} height={h - (k === 'oven' ? 180 : 120)} fill="#0b0b0c" stroke="#555" strokeWidth={3} />
          <path d={`M${x + 70} ${y + h * 0.75} l${w * 0.3} -${h * 0.35}`} stroke="#fff" strokeWidth={5} opacity="0.12" />
          <Handle x={x + 60} y={y + 50} w={w - 120} h={12} c={c} />
        </g>
      );
    case 'fridge':
      return <FrontSvg x={x + g} y={y + g} w={w - 2 * g} h={h - 2 * g} mat={c.front} c={c} handle={{ x: x + w - 44, y: y + 60, w: 12, h: Math.min(700, h * 0.45) }} />;
    case 'washer':
    case 'dryer': {
      const r = Math.min(w, h) * 0.3;
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} glow={false} />
          <rect x={x + 25} y={y + 25} width={w - 50} height={h - 40} rx={14} fill="#eceae6" stroke={INK} strokeWidth={s} />
          <path d={`M${x + 25} ${y + 120}h${w - 50}`} stroke={INK} strokeWidth={s * 0.6} />
          <circle cx={x + w - 90} cy={y + 72} r={22} fill="#cfcac2" stroke={INK} strokeWidth={2} />
          <circle cx={x + w / 2} cy={y + 120 + (h - 160) / 2} r={r} fill="#c9d2d5" stroke={INK} strokeWidth={s} />
          <circle cx={x + w / 2} cy={y + 120 + (h - 160) / 2} r={r * 0.7} fill={k === 'washer' ? '#9fb2b8' : '#d8dcdd'} stroke={INK} strokeWidth={2} />
        </g>
      );
    }
    case 'wine':
      return <g><Interior x={x} y={y} w={w} h={h} c={c} /><rect x={x + 20} y={y + 20} width={w - 40} height={h - 40} fill={`url(#${c.p}-wine)`} stroke={INK} strokeWidth={s} /><rect x={x + g} y={y + g} width={w - 2 * g} height={h - 2 * g} fill={`url(#${c.p}-glass)`} stroke={c.F(c.mats.metal)} strokeWidth={12} /></g>;
    case 'shoes': {
      const n = Math.max(1, Math.floor(h / 240));
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} />
          {Array.from({ length: n }).map((_, i) => {
            const sy = y + (i + 1) * (h / n) - 20;
            const pairs = Math.max(1, Math.floor((w - 40) / 150));
            return (
              <g key={i}>
                <path d={`M${x} ${sy}L${x + w} ${sy - 50}`} stroke={c.F(c.front)} strokeWidth={22} />
                {Array.from({ length: pairs }).map((__, j) => {
                  const px = x + 30 + j * ((w - 60) / pairs), py = sy - 12 - ((px - x) / w) * 50;
                  return <path key={j} d={`M${px} ${py} q10 -55 45 -60 q25 0 30 20 q35 10 50 40z`} fill={CLOTHES[(i * 3 + j + 4) % CLOTHES.length]} stroke={INK} strokeWidth={s * 0.5} />;
                })}
              </g>
            );
          })}
        </g>
      );
    }
    case 'books': {
      const n = Math.max(2, Math.round(h / 330)), sp = h / n;
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} />
          {Array.from({ length: n }).map((_, i) => {
            const by = y + i * sp + 22; const bh = sp - 22; const books = []; let bx = x + 20;
            for (let j = 0; bx < x + w - 60 && j < 40; j++) {
              if (rnd(seed + i * 13 + j) < 0.08) { bx += 70; continue; }
              const ww = 22 + rnd(seed * 2 + j + i) * 26, hh = bh * (0.6 + rnd(seed + j * 3 + i) * 0.32);
              books.push(<rect key={j} x={bx} y={by + bh - hh} width={ww} height={hh} fill={BOOKS[(i * 5 + j + seed) % BOOKS.length]} stroke={INK} strokeWidth={s * 0.35} />);
              bx += ww + 1.5;
            }
            return <g key={i}>{i > 0 && <Shelf x={x} y={y + i * sp} w={w} c={c} />}{books}</g>;
          })}
        </g>
      );
    }
    case 'coffee':
      return (
        <g>
          <Interior x={x} y={y} w={w} h={h} c={c} />
          <rect x={x + w * 0.22} y={y + h * 0.3} width={w * 0.56} height={h * 0.66} rx={14} fill={c.F('inox')} stroke={INK} strokeWidth={s} />
          <rect x={x + w * 0.4} y={y + h * 0.55} width={w * 0.2} height={34} fill="#1d1d1e" />
          <rect x={x + w * 0.44} y={y + h * 0.78} width={w * 0.12} height={h * 0.12} fill="#f2efe9" stroke={INK} strokeWidth={2} />
        </g>
      );
    case 'tv':
      return <g><Interior x={x} y={y} w={w} h={h} c={c} glow={false} /><rect x={x + w * 0.08} y={y + 40} width={w * 0.84} height={h - 80} fill="#0e0f10" stroke={INK} strokeWidth={s} /><path d={`M${x + w * 0.15} ${y + h - 60}L${x + w * 0.45} ${y + 60}`} stroke="#fff" strokeWidth={30} opacity="0.05" /></g>;
    case 'mirror':
      return (
        <g>
          {c.led && <rect x={x + 20} y={y + 20} width={w - 40} height={h - 40} rx={40} fill="#ffcf8a" filter={`url(#${c.p}-glow)`} opacity="0.9" />}
          <rect x={x + 30} y={y + 30} width={w - 60} height={h - 60} rx={40} fill={`url(#${c.p}-mirror)`} stroke={INK} strokeWidth={s} />
          <path d={`M${x + w * 0.25} ${y + h * 0.7}L${x + w * 0.55} ${y + h * 0.25}`} stroke="#fff" strokeWidth={8} opacity="0.55" />
        </g>
      );
    case 'basket':
      return <g><Interior x={x} y={y} w={w} h={h} c={c} glow={false} /><rect x={x + 30} y={y + 25} width={w - 60} height={h - 35} rx={20} fill={`url(#${c.p}-wicker)`} stroke={INK} strokeWidth={s} /></g>;
    case 'logs': {
      const logs = [];
      for (let yy = y + h - 60; yy > y + 50; yy -= 95) for (let xx = x + 60; xx < x + w - 40; xx += 100) {
        const off = ((yy / 95) | 0) % 2 ? 50 : 0;
        if (xx + off < x + w - 50) logs.push(<g key={`${xx}-${yy}`}><circle cx={xx + off} cy={yy} r={44} fill="#8a6a4a" stroke={INK} strokeWidth={s * 0.5} /><circle cx={xx + off} cy={yy} r={22} fill="none" stroke="#5f4630" strokeWidth={3} /></g>);
      }
      return <g><Interior x={x} y={y} w={w} h={h} c={c} glow={false} />{logs}</g>;
    }
    case 'hood':
      return <g><path d={`M${x + w * 0.08} ${y + h}L${x + w * 0.3} ${y + h * 0.25}V${y - (c.ceil ?? 0)}H${x + w * 0.7}V${y + h * 0.25}L${x + w * 0.92} ${y + h}z`} fill={c.F(c.front)} stroke={INK} strokeWidth={s} /><rect x={x + w * 0.06} y={y + h - 50} width={w * 0.88} height={50} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s} /></g>;
    case 'slab':
      return <g><rect x={x} y={y} width={w} height={h} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />{c.led && <rect x={x + 10} y={y + h} width={w - 20} height={130} fill={`url(#${c.p}-led)`} opacity="0.8" />}</g>;
    default:
      return null;
  }
}

export const layoutParts = (parts, top, bottom) => {
  const fixed = parts.reduce((s, q) => s + (q.h || 0), 0);
  const nFlex = parts.filter((q) => !q.h).length;
  const fh = nFlex ? Math.max(80, (bottom - top - fixed) / nFlex) : 0;
  let y = top;
  return parts.map((q) => { const h = q.h || fh; const r = { ...q, y, h }; y += h; return r; });
};

export function Stack({ parts, x, y, w, h, c, seed, zone }) {
  return layoutParts(parts, y, y + h).map((q, i) => (
    <Part key={i} k={q.k} x={x} y={q.y} w={w} h={q.h} c={c} seed={seed + i}
      hpos={zone === 'base' ? 'top' : i === 0 && parts.length > 1 ? 'bottom' : 'mid'} />
  ));
}

function TopFixture({ top, w, c }) {
  const s = c.sw;
  const faucet = (fx) => <path key={fx} d={`M${fx} 0 v-230 q0 -60 60 -60 q55 0 55 60 v40`} fill="none" stroke={c.F(c.mats.metal)} strokeWidth={20} strokeLinecap="round" />;
  if (top === 'sink') return faucet(w / 2 - 30);
  if (top === 'basin' || top === 'basin2') {
    const xs = top === 'basin' ? [w / 2] : [w * 0.27, w * 0.73];
    return xs.map((bx) => (
      <g key={bx}>
        <path d={`M${bx - 210} 0 q0 -150 210 -150 q210 0 210 150z`} fill="#f5f3ef" stroke={INK} strokeWidth={s} />
        <rect x={bx - 14} y={-340} width={28} height={110} rx={10} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s * 0.5} />
      </g>
    ));
  }
  if (top === 'bbq') return <g><path d={`M${w * 0.12} 0 q0 -260 ${w * 0.38} -260 q${w * 0.38} 0 ${w * 0.38} 260z`} fill="#2a2a2b" stroke={INK} strokeWidth={s} /><Handle x={w * 0.35} y={-200} w={w * 0.3} h={14} c={c} /></g>;
  if (top === 'plancha') return <rect x={40} y={-30} width={w - 80} height={30} fill="#4a4a4a" stroke={INK} strokeWidth={s} />;
  return null;
}

function FreeFront({ kind, w, h, c }) {
  const s = c.sw;
  if (kind === 'shower') return (
    <g>
      <rect x={0} y={0} width={w} height={h} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <path d={`M0 ${h / 3}h${w}M0 ${(2 * h) / 3}h${w}M${w / 2} 0v${h}`} stroke={INK} strokeWidth={s * 0.4} opacity="0.35" />
      <Interior x={w * 0.6} y={h * 0.42} w={w * 0.3} h={320} c={c} />
      <path d={`M${w * 0.35} -400 V60`} stroke={c.F(c.mats.metal)} strokeWidth={18} />
      <rect x={w * 0.35 - 160} y={60} width={320} height={18} rx={9} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s * 0.6} />
      {[-120, -70, -20, 30, 80, 130].map((dx) => <path key={dx} d={`M${w * 0.35 + dx} 110 v${h * 0.55}`} stroke="#9fc6d1" strokeWidth={4} strokeDasharray="30 40" opacity="0.7" />)}
      <rect x={w * 0.82} y={h * 0.55} width={70} height={160} rx={10} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s * 0.6} />
      {!c.persp && <rect x={0} y={0} width={w * 0.55} height={h - 10} fill={`url(#${c.p}-glass)`} stroke={c.F('noir_mat')} strokeWidth={14} />}
      {!c.persp && <path d={`M0 ${h * 0.5}h${w * 0.55}`} stroke={c.F('noir_mat')} strokeWidth={10} />}
    </g>
  );
  if (kind === 'bath') return (
    <g>
      <path d={`M${w * 0.82} 0 v-420 q0 -60 -70 -60 h-60`} fill="none" stroke={c.F(c.mats.metal)} strokeWidth={18} strokeLinecap="round" />
      <rect x={0} y={0} width={w} height={h} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <rect x={0} y={0} width={w} height={40} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
    </g>
  );
  if (kind === 'wc') return (
    <g>
      <rect x={w / 2 - 125} y={0} width={250} height={170} rx={10} fill={c.F(c.mats.metal)} stroke={INK} strokeWidth={s} />
      <circle cx={w / 2 - 45} cy={85} r={36} fill="none" stroke={INK} strokeWidth={3} /><circle cx={w / 2 + 45} cy={85} r={36} fill="none" stroke={INK} strokeWidth={3} />
      <path d={`M${w / 2 - 190} ${h - 420} h380 v40 q0 120 -120 140 h-140 q-120 -20 -120 -140z`} fill="#f4f3f0" stroke={INK} strokeWidth={s} />
    </g>
  );
  if (kind === 'desk') return (
    <g>
      <rect x={0} y={40} width={40} height={h - 40} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
      <rect x={w - 520} y={40} width={500} height={190} fill={c.F(c.accent)} stroke={INK} strokeWidth={s} />
      <Handle x={w - 370} y={80} w={200} h={12} c={c} />
      <rect x={w - 40} y={40} width={40} height={h - 40} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
      <rect x={0} y={0} width={w} height={40} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
    </g>
  );
  if (kind === 'table') return (
    <g>
      <rect x={w * 0.12} y={50} width={160} height={h - 50} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <rect x={w * 0.88 - 160} y={50} width={160} height={h - 50} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <rect x={0} y={0} width={w} height={50} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
    </g>
  );
  if (kind === 'bench') return (
    <g>
      <rect x={90} y={60} width={320} height={h - 60} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <rect x={w - 410} y={60} width={320} height={h - 60} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      <rect x={0} y={0} width={w} height={60} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
    </g>
  );
  if (kind === 'planter') {
    const greens = ['#5d7a52', '#3f5c3c', '#7d9a6a', '#4b6b45'];
    const n = Math.max(3, Math.round(w / 180));
    return (
      <g>
        {Array.from({ length: n }).map((_, i) => {
          const px = (w / n) * (i + 0.5), ph = h * (0.9 + rnd(i + w) * 0.7);
          return <path key={i} d={`M${px} ${h * 0.45} C${px - 160} ${h * 0.3} ${px - 120} ${h * 0.45 - ph} ${px} ${h * 0.45 - ph} C${px + 120} ${h * 0.45 - ph} ${px + 160} ${h * 0.3} ${px} ${h * 0.45}z`} fill={greens[i % 4]} stroke={INK} strokeWidth={s * 0.6} opacity="0.92" />;
        })}
        <rect x={0} y={h * 0.45} width={w} height={h * 0.55} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
      </g>
    );
  }
  return null;
}

export function ModuleFront({ it, c }) {
  const { spec, w, h } = it;
  const s = c.sw, seed = it.idx * 11 + 3;
  if (spec.zone === 'free') return <FreeFront kind={spec.free} w={w} h={h} c={c} />;
  if (spec.zone === 'base') return (
    <g>
      <TopFixture top={spec.top} w={w} c={c} />
      <rect x={0} y={40} width={w} height={h - 140} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
      <Stack parts={spec.parts} x={0} y={40} w={w} h={h - 140} c={c} seed={seed} zone="base" />
      <rect x={30} y={h - 100} width={w - 30} height={100} fill="#1a1a1a" opacity="0.7" />
      <rect x={0} y={0} width={w} height={40} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />
    </g>
  );
  if (spec.zone === 'float') {
    const t = spec.top ? 30 : 0;
    return (
      <g>
        <TopFixture top={spec.top} w={w} c={c} />
        <rect x={0} y={t} width={w} height={h - t} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
        <Stack parts={spec.parts} x={0} y={t} w={w} h={h - t} c={c} seed={seed} zone="tall" />
        {t > 0 && <rect x={0} y={0} width={w} height={t} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={s} />}
        {c.led && <rect x={20} y={h} width={w - 40} height={160} fill={`url(#${c.p}-led)`} opacity="0.7" />}
      </g>
    );
  }
  return (
    <g>
      <rect x={0} y={0} width={w} height={h - 100} fill={c.F(c.front)} stroke={INK} strokeWidth={s} />
      <Stack parts={spec.parts} x={18} y={18} w={w - 36} h={h - 136} c={c} seed={seed} zone="tall" />
      <rect x={30} y={h - 100} width={w - 30} height={100} fill="#1a1a1a" opacity="0.7" />
    </g>
  );
}

export function UpperFront({ up, w, c, seed }) {
  return (
    <g>
      {up.parts[0].k !== 'hood' && up.parts[0].k !== 'slab' && up.parts[0].k !== 'mirror' && <rect x={0} y={0} width={w} height={up.h} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} />}
      <Stack parts={up.parts} x={0} y={0} w={w} h={up.h} c={c} seed={seed} zone="upper" />
    </g>
  );
}

// Top-surface primitives in plan coordinates (x, z from back wall)
export function topPrims(it, c) {
  const { spec, x, w, d } = it;
  const cx = x + w / 2, P = [];
  const metal = c.F(c.mats.metal);
  if (spec.top === 'sink') P.push({ t: 'rect', x: cx - Math.min(260, w * 0.35), z: 90, w: Math.min(520, w * 0.7), d: 400, fill: '#d6dadc' }, { t: 'circle', cx, cz: 290, r: 28, fill: '#8d9396' }, { t: 'circle', cx: cx - 30, cz: 45, r: 26, fill: metal });
  if (spec.top === 'hob') { P.push({ t: 'rect', x: cx - 300, z: 60, w: 600, d: 480, fill: '#151515' }); [[-140, 170], [140, 170], [-140, 410], [140, 410]].forEach(([dx, dz], i) => P.push({ t: 'circle', cx: cx + dx, cz: dz, r: i % 2 ? 80 : 100, fill: 'none', stroke: '#7a7a7a' })); }
  if (spec.top === 'basin' || spec.top === 'basin2') (spec.top === 'basin' ? [cx] : [x + w * 0.27, x + w * 0.73]).forEach((bx) => P.push({ t: 'circle', cx: bx, cz: d * 0.55, r: 200, fill: '#f5f3ef', stroke: INK }, { t: 'circle', cx: bx, cz: d * 0.55, r: 30, fill: '#9a9a9a' }));
  if (spec.top === 'bbq') { P.push({ t: 'rect', x: cx - 350, z: 60, w: 700, d: 460, fill: '#2a2a2b' }); for (let i = 1; i < 8; i++) P.push({ t: 'line', x1: cx - 350 + i * 87, z1: 80, x2: cx - 350 + i * 87, z2: 500, stroke: '#777' }); }
  if (spec.top === 'plancha') P.push({ t: 'rect', x: x + 50, z: 60, w: w - 100, d: 440, fill: '#4a4a4a' });
  return P;
}

// Side-wall fronts rendered through a perspective mapping (lx, ly) -> screen
export function ProjFront({ it, c, map, h, parts, zone, top }) {
  const w = it.w;
  const quad = (x0, y0, x1, y1) => [map(x0, y0), map(x1, y0), map(x1, y1), map(x0, y1)].map((q) => q.join(',')).join(' ');
  const out = [];
  const bodyTop = zone === 'base' ? 40 : 0, bodyBot = zone === 'base' || zone === 'tall' ? h - 100 : h;
  out.push(<polygon key="body" points={quad(0, bodyTop, w, bodyBot)} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} />);
  const fixed = parts.reduce((a, q) => a + (q.h || 0), 0), nf = parts.filter((q) => !q.h).length;
  const pad = zone === 'tall' ? 18 : 0;
  const fh = nf ? Math.max(80, (bodyBot - bodyTop - 2 * pad - fixed) / nf) : 0;
  let y = bodyTop + pad;
  parts.forEach((q, i) => {
    const ph = q.h || fh, x0 = pad, x1 = w - pad, y0 = y, y1 = y + ph;
    const k = q.k;
    if (k === 'door' || k === 'fridge') {
      const n = w > 650 ? 2 : 1;
      for (let j = 0; j < n; j++) {
        const a = x0 + ((x1 - x0) / n) * j + 4, b = x0 + ((x1 - x0) / n) * (j + 1) - 4;
        out.push(<polygon key={`d${i}${j}`} points={quad(a, y0 + 4, b, y1 - 4)} fill={c.F(c.front)} stroke={INK} strokeWidth={c.sw} />);
        const hx = n === 2 && j === 0 ? b - 40 : n === 2 ? a + 34 : b - 40;
        out.push(<polygon key={`h${i}${j}`} points={quad(hx, y0 + ph * 0.35, hx + 12, y0 + ph * 0.35 + Math.min(200, ph * 0.3))} fill={c.F(c.mats.metal)} />);
      }
    } else if (k === 'drawer') {
      out.push(<polygon key={`w${i}`} points={quad(x0 + 4, y0 + 4, x1 - 4, y1 - 4)} fill={c.F(c.accent)} stroke={INK} strokeWidth={c.sw} />);
      out.push(<polygon key={`wh${i}`} points={quad(w * 0.32, y0 + 40, w * 0.68, y0 + 52)} fill={c.F(c.mats.metal)} />);
    } else if (k === 'oven' || k === 'micro' || k === 'tv') {
      out.push(<polygon key={`o${i}`} points={quad(x0 + 4, y0 + 4, x1 - 4, y1 - 4)} fill="#1b1b1c" stroke={INK} strokeWidth={c.sw} />);
    } else if (k === 'mirror') {
      out.push(<polygon key={`m${i}`} points={quad(x0 + 30, y0 + 30, x1 - 30, y1 - 30)} fill={`url(#${c.p}-mirror)`} stroke={INK} strokeWidth={c.sw} />);
    } else if (k !== 'void' && k !== 'hood' && k !== 'slab') {
      out.push(<polygon key={`i${i}`} points={quad(x0, y0, x1, y1)} fill="#000" opacity="0.34" />);
      if (c.led) out.push(<polygon key={`l${i}`} points={quad(x0, y0, x1, y0 + Math.min(200, ph * 0.5))} fill={`url(#${c.p}-led)`} />);
      if (k === 'hang' || k === 'hangLong') {
        const nG = Math.max(1, Math.floor((x1 - x0 - 60) / 55));
        for (let g = 0; g < nG; g++) {
          const gx = x0 + 35 + g * ((x1 - x0 - 70) / nG);
          out.push(<polygon key={`g${i}${g}`} points={quad(gx, y0 + 70, gx + 40, y0 + 70 + ph * (k === 'hang' ? 0.68 : 0.85))} fill={CLOTHES[(g + i + it.idx) % CLOTHES.length]} stroke={INK} strokeWidth={1.5} />);
        }
      } else {
        const nS = Math.max(1, Math.round(ph / 360));
        for (let sI = 1; sI < nS; sI++) out.push(<polygon key={`s${i}${sI}`} points={quad(x0, y0 + (ph / nS) * sI - 11, x1, y0 + (ph / nS) * sI + 11)} fill={c.F(c.front)} stroke={INK} strokeWidth={1.5} />);
      }
      if (k === 'glass' || k === 'wine') out.push(<polygon key={`gl${i}`} points={quad(x0 + 4, y0 + 4, x1 - 4, y1 - 4)} fill={`url(#${c.p}-glass)`} stroke={c.F(c.mats.metal)} strokeWidth={8} />);
    }
    y = y1;
  });
  if (zone === 'base' || zone === 'tall') out.push(<polygon key="pl" points={quad(30, h - 100, w, h)} fill="#1a1a1a" opacity="0.7" />);
  if (top) out.push(<polygon key="wt" points={quad(0, 0, w, top)} fill={c.F(c.mats.top)} stroke={INK} strokeWidth={c.sw} />);
  return <g>{out}</g>;
}
